import { afterNextRender, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ResourcesAgenciesComponent } from './modules/admin/pages/resources/agencies/agencies.component';
import { TFMService } from './mock-api/tfm.service';
import { filter } from 'rxjs';
import { FuseConfigService } from '@fuse/services/config';
import { ToastrService } from 'ngx-toastr';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

declare const gtag: Function;
declare const fbq: Function;

@Component({
    selector   : 'app-root',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss'],
    standalone : true,
    imports    : [CommonModule,RouterOutlet,ResourcesAgenciesComponent],
})
export class AppComponent implements OnInit {
    public view : any = {};

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private tfmService: TFMService,
        private _fuseConfigService: FuseConfigService,
        private toaster: ToastrService,
        private title: Title,
        private meta: Meta,
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject(DOCUMENT) private document: Document
    ) {
        // Browser-only initialization (analytics, chat, localStorage, query-param routing)
        afterNextRender(() => {
            this.initBrowser();
        });
    }

    /**
     * SSR-safe initialisation: runs on both server and client.
     * On the server the pre-fetched site config is used so the rendered HTML
     * includes real SEO meta tags, title, and OG data for crawlers.
     */
    ngOnInit(): void {
        this.view.isClient = this.tfmService.view.isClient;

        if (!isPlatformBrowser(this.platformId) && this.view.isClient) {
            this.tfmService.getSite().then(site => {
                if (site) {
                    this.view.site = site;
                    this.applySeo(site);
                    const profile = this.tfmService.getSiteProfile(site.profileID);
                    if (profile) {
                        this.view.site.profile = profile;
                        this._fuseConfigService.config = {
                            layout: profile.layout,
                            theme: profile.theme,
                            scheme: profile.scheme,
                            defaultThemeStylesheet: this.getThemeStylesheetForSiteCode(site.code) ?? undefined
                        };
                    }
                }
            }).catch(() => { /* build-time site not available – static shell is fine */ });
        }
    }

    /** Resolve B2C site from ?ai=&sc= (shared by client mode and localhost platform preview). */
    private applyClientSiteFromAiScParams(agencyID: string, siteCode: string): void {
        this.tfmService.view.isClient = true;
        this.view.isClient = true;
        this.tfmService.setChannelType('client');

        this.tfmService.getSites(agencyID).subscribe((res) => {
            const site = res.content.filter(
                (s) => s.code && s.code.toLowerCase() === siteCode.toLowerCase()
            );

            if (site && site.length === 1) {
                this.tfmService.changeSite(site[0]);

                this.view.site = site[0];

                this.view.site.profile = this.tfmService.getSiteProfile(this.view.site.profileID);

                this.tfmService.log('setting site config', this.view);

                this.applySeo(this.view.site);

                this._fuseConfigService.config = {
                    layout: this.view.site.profile.layout,
                    theme: this.view.site.profile.theme,
                    scheme: this.view.site.profile.scheme,
                    defaultThemeStylesheet: this.getThemeStylesheetForSiteCode(this.view.site.code) ?? undefined,
                };
            }
        });
    }

    /** Map site code to body class for site-specific stylesheet (e.g. ai=23&sc=ET → theme-stylesheet-xlt). */
    private getThemeStylesheetForSiteCode(code: string | null | undefined): string | null {
        if (!code) return null;
        const c = code.toLowerCase().replace(/[-\s]/g, '');
        const map: Record<string, string> = {
            xlt: 'theme-stylesheet-xlt', xlit: 'theme-stylesheet-xlt', xlttp: 'theme-stylesheet-xlt', kst: 'theme-stylesheet-xlt',
            ovt: 'theme-stylesheet-xlt', xlth: 'theme-stylesheet-xlh', sn: 'theme-stylesheet-sn',
            whitestar: 'theme-stylesheet-ws', ws: 'theme-stylesheet-ws', et: 'theme-stylesheet-xlt'  // XL Elite Travel
        };
        return map[c] ?? null;
    }

    /**
     * Runs only in the browser after Angular hydration.
     * Handles domain detection, query-param routing, analytics, chat, affiliates, etc.
     */
    private initBrowser(): void {
        var domain = window.location.hostname;
        this.view.isClient = this.tfmService.view.isClient;

        // On full page load/refresh, clear quote and cart session so the cart does not persist
        this.tfmService.clearLocalStorage('cart');
        this.tfmService.clearLocalStorage('trip');
        this.tfmService.clearLocalStorage('itin');
        this.tfmService.clearLocalStorage('itinRef');
        this.tfmService.clearLocalItinGuid();

        if (this.view.isClient) {
            this.tfmService.setChannelType('client');

            if (
                domain == "yoursite.travelfind.me"
                || domain == "b2c.travelfind.me"
                || domain == "xltravel.travelfind.me"
                || domain == "localhost"
                || domain == "127.0.0.1"
            ) {
                this.route.queryParamMap.subscribe(params => {
                    if (params && params.get("ai") && params.get("sc")) {
                        this.applyClientSiteFromAiScParams(params.get("ai")!, params.get("sc")!);
                    }
                    else {
                        this.tfmService.getSite().then(res => {
                            this.view.site = res;

                            this.applySeo(this.view.site);

                            if (res.id == 33)
                                this.setChat(res);
                        });
                    }
                });
            }
            else {
                this.tfmService.getSiteByDomain(domain).subscribe(res => {
                    console.log("site domain", res);
                    this.tfmService.changeSite(res);

                    this.setAnalytics(res);
                    this.setFBPixel(res);

                    this.tfmService.getSite().then(res => {
                        this.view.site = res;
                        console.log("site", this.view);

                        this.applySeo(this.view.site);

                        this.view.site.profile = this.tfmService.getSiteProfile(this.view.site.profileID);

                        this._fuseConfigService.config = {
                            layout: this.view.site.profile.layout,
                            theme: this.view.site.profile.theme,
                            scheme: this.view.site.profile.scheme,
                            defaultThemeStylesheet: this.getThemeStylesheetForSiteCode(this.view.site.code) ?? undefined
                        };
                    });

                    this.router.events.pipe(
                        filter(event => event instanceof NavigationEnd)
                    ).subscribe((event: NavigationEnd) => {
                        if (res.trackingID)
                            this.trackAnalytics(event.urlAfterRedirects);

                        if (res.pixelID)
                            this.trackFBPixel(event.urlAfterRedirects);
                    })

                    if (res.id == 33)
                        this.setChat(res);
                });
            }

            this.route.queryParamMap.subscribe(params => {
                if (params) {
                    if (params.get("ac")) {
                        var affiliateCode = params.get("ac");

                        if (affiliateCode == "0")
                            this.tfmService.clearAffiliate();
                        else if (affiliateCode)
                            this.tfmService.setAffiliateCookie(affiliateCode);
                    }

                    if (params.get("s")) {
                        var statusText = params.get("s");
                    }
                }
            });
        }
        else if (
            domain == "platform-v2.travelfind.me"
            || domain == "platform-v2-uat.travelfind.me"
            || domain == "localhost"
            || domain == "127.0.0.1"
        ) {
            this.tfmService.setChannelType('');

            // Localhost opened without ?ai=&sc= starts in platform mode; subscribe so adding params later still loads the B2C site.
            if (domain == "localhost" || domain == "127.0.0.1") {
                this.route.queryParamMap.subscribe((params) => {
                    const ai = params?.get('ai');
                    const sc = params?.get('sc');
                    if (ai && sc) {
                        this.applyClientSiteFromAiScParams(ai, sc);
                    }
                });
            }

            this.route.queryParamMap.subscribe(params => {
                if (params && params.get("c")) {
                    var clear = params.get("c");

                    localStorage.setItem("agencyID", "23");
                    localStorage.setItem("siteID", "47");
                    localStorage.setItem("siteGUID", "1bf5cbd4-ed0a-4759-87da-f3a630419826");

                    localStorage.setItem("layout", "dense");
                    localStorage.setItem("theme", "theme-brand");
                    localStorage.setItem("scheme", "light");

                    window.location.href = window.location.href.split("?")[0];
                }
                else if (domain == "platform-v2.travelfind.me" || domain == "platform-v2-uat.travelfind.me") {
                    var site = this.tfmService.getLocalAgencySite();

                    this.setPushNotifications();

                    if (site && (site.agencyID == 23 || site.siteID == 47)) {
                        if (localStorage.getItem("layout") != "dense" || localStorage.getItem("theme") != "theme-brand") {
                            localStorage.setItem("layout", "dense");
                            localStorage.setItem("theme", "theme-brand");

                            window.location.reload();
                        }
                    }
                    else if (site) {
                        if (localStorage.getItem("layout") != "dense" || localStorage.getItem("theme") != "theme-brand") {
                            localStorage.setItem("layout", "dense");
                            localStorage.setItem("theme", "theme-brand");

                            window.location.reload();
                        }
                    }
                }
            });
        }
    }

    private applySeo(site: any): void {
        if (!site) {
            return;
        }

        const isBrowser = isPlatformBrowser(this.platformId);

        if (isBrowser && this.view.isClient) {
            this.setFavicon(site.favIconUrl);
        }

        const hostnameFallback = isBrowser
            ? (window.location?.hostname || 'Travel platform')
            : (site?.website || site?.name || 'Travel platform');

        const rawTitle = site?.metaTitle || site?.seoTitle || site?.name || hostnameFallback;
        let cleanedTitle = rawTitle;

        if (this.view.isClient) {
            cleanedTitle = cleanedTitle.replace(/\s*[-–—]\s*Travel Platform\s*[-–—]\s*TFM\s*$/i, '');
            cleanedTitle = cleanedTitle.replace(/\s*[-–—]\s*Travel Platform\s*[-–—]\s*V2\s*$/i, '');
            cleanedTitle = cleanedTitle.trim();
        }

        const sloganSuffix = (site?.slogan && site.slogan !== 'null') ? ` - ${site.slogan}` : '';
        const finalTitle = `${cleanedTitle || rawTitle}${sloganSuffix}`;
        this.title.setTitle(finalTitle);

        const description = (site?.metaDescription && site.metaDescription !== 'null' ? site.metaDescription : null)
            || (site?.slogan && site.slogan !== 'null' ? site.slogan : null)
            || 'Travel packages, flights and tailor-made holidays.';
        this.meta.updateTag({ name: 'description', content: description });

        const keywords = site?.metaKeywords || 'travel, holidays, flights, accommodation';
        this.meta.updateTag({ name: 'keywords', content: keywords });

        const canonicalUrl =
            site?.canonicalUrl ||
            (isBrowser ? window.location.origin + window.location.pathname : null);
        if (canonicalUrl) {
            let linkEl = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
            if (!linkEl) {
                linkEl = this.document.createElement('link');
                linkEl.rel = 'canonical';
                this.document.head.appendChild(linkEl);
            }
            linkEl.href = canonicalUrl;
        }

        const imageUrl = site?.metaImageUrl || site?.imageUrl || site?.logoUrl || site?.favIconUrl || '';
        this.meta.updateTag({ property: 'og:title', content: finalTitle });
        this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        if (canonicalUrl) {
            this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
        }
        if (imageUrl) {
            this.meta.updateTag({ property: 'og:image', content: imageUrl });
        }
        if (site?.name) {
            this.meta.updateTag({ property: 'og:site_name', content: site.name });
        }

        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: finalTitle });
        this.meta.updateTag({ name: 'twitter:description', content: description });
        if (imageUrl) {
            this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
        }
    }

    setAnalytics(site) {
        if (site.trackingID) {
            const gTagManagerScript = this.document.createElement('script');
            gTagManagerScript.async = true;
            gTagManagerScript.src = `https://www.googletagmanager.com/gtag/js?id=${site.trackingID}`;
            this.document.head.appendChild(gTagManagerScript);

            const gaScript = this.document.createElement('script');
            gaScript.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag() { dataLayer.push(arguments); }
            gtag('js', new Date());
            gtag('config', '${site.trackingID}', {send_page_view: false});
            `;
            this.document.head.appendChild(gaScript);
        }
    }
    trackAnalytics(path) {
        gtag('event', 'page_view', {
            page_path: path
            });
    }

    setFBPixel(site) {
        if (site.pixelID) {
            const scriptItemUrl = this.document.createElement('script');
            scriptItemUrl.async = true;
            scriptItemUrl.src = `https://www.facebook.com/tr?id=${site.pixelID}&ev=PageView&noscript=1`;
            this.document.head.appendChild(scriptItemUrl);

            const gaScript = this.document.createElement('script');
            gaScript.innerHTML = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${site.pixelID}');
            `;
            this.document.head.appendChild(gaScript);
        }
    }
    trackFBPixel(path) {
        if (typeof fbq === 'function') {
            fbq('track', 'PageView');
        }
    }

    setChat(site) {
        if (true) {
            const scriptTag = this.document.createElement('script');
            scriptTag.async = true;
            scriptTag.type = "text/javascript";
            scriptTag.src = `https://www.formilla.com/scripts/feedback.js`;
            this.document.head.appendChild(scriptTag);

            scriptTag.onload = (e) => {
                const gaScript = this.document.createElement('script');
                gaScript.innerHTML = `
            Formilla.guid = 'cs5adee9-e700-4829-bb7e-a93985852a3c';
            Formilla.loadWidgets();
            `;
                this.document.head.appendChild(gaScript);
            };
        }
    }

    applyTheme(key: string) {
        if (!key || key === 'null' || key.includes('null.css')) {
            return null;
        }
        const linkEl = this.document.createElement('link');
        linkEl.setAttribute('rel', 'stylesheet');
        linkEl.setAttribute('href', key);
        this.document.head.appendChild(linkEl);
        return linkEl;
    }
    setFavicon(faviconUrl: string): void {
        const HTMLlinkElement = this.document.querySelector<HTMLLinkElement>('link[rel="icon"]');

        if (HTMLlinkElement) {
            HTMLlinkElement.href = faviconUrl;
        }
        else {
            const newLinkElement = this.document.createElement('link');
            newLinkElement.rel = 'icon';
            newLinkElement.href = faviconUrl;
            this.document.head.appendChild(newLinkElement);
        }
    }


    setPushNotifications(site = null) {
        console.log("setPushNotifications");
        var userId = this.tfmService.getLoggedInUserId();
        if (userId > 0) {
            const script = this.document.createElement('script');
            script.async = true;
            script.src = `https://cdn.by.wonderpush.com/sdk/1.1/wonderpush-loader.min.js`;
            this.document.head.appendChild(script);

            this.tfmService.getLoggedInUser().subscribe(resUser => {
                var agencyId = resUser.agencyId;

                this.tfmService.getAgencies().subscribe(resAgencies => {
                    var agency = resAgencies.content.filter(x=>x.id == agencyId)[0];
                    const gaScript = this.document.createElement('script');
                    gaScript.innerHTML = `
                    window.WonderPush = window.WonderPush || [];
                    WonderPush.push(['setUserId', "${userId}"]);
                    WonderPush.push(['putProperties', {
                    "int_agencyId": ${agencyId},
                    "string_agencyName": "${agency.name}",
                    "string_firstName": "${resUser.firstName}",
                    "string_lastName": "${resUser.lastName}"
                    }]);
                    WonderPush.push(["init", {
                        webKey: "14d1ecbaad4560f8591ab9321971ea3458ac1ff66eb00473eed537d616f5a43d",
                    }]);
                    `;
                    this.document.head.appendChild(gaScript);
                });
            });
        }
    }
}
